import { useState, useRef, useCallback, useEffect } from 'react';
import { RoundProps } from './types';

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex justify-center gap-2 mb-5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`rounded-full transition-all duration-300 ${
          i < current ? 'w-8 h-5 bg-green-400' : i === current ? 'w-8 h-5 bg-amber-400 animate-pulse' : 'w-5 h-5 bg-gray-200'
        }`} />
      ))}
    </div>
  );
}

function WrongWarning({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => { const t = setTimeout(onDismiss, 2500); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-8 py-5 rounded-2xl text-lg font-bold shadow-lg animate-bounce max-w-sm text-center">
      🚫 {message}
    </div>
  );
}

function Instruction({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-6 py-4 text-center w-full max-w-xl mx-auto">
      <p className="text-lg font-extrabold text-amber-800">{text}</p>
      {sub && <p className="text-base text-amber-600 mt-1">{sub}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 1: PHÂN LOẠI DỤNG CỤ NẤU ĂN VÀ THỰC PHẨM NẤU ĂN
// ══════════════════════════════════════════════════════
const ALL_ITEMS = [
  { id: 1, emoji: '🔪', label: 'Dao', type: 'tool' as const },
  { id: 2, emoji: '🪣', label: 'Thau', type: 'tool' as const },
  { id: 3, emoji: '🥄', label: 'Muỗng', type: 'tool' as const },
  { id: 4, emoji: '🧻', label: 'Khăn lau', type: 'tool' as const },
  { id: 5, emoji: '🎋', label: 'Ống nứa', type: 'tool' as const },
  { id: 6, emoji: '🪵', label: 'Củi', type: 'tool' as const },
  { id: 7, emoji: '💧', label: 'Nước', type: 'food' as const },
  { id: 8, emoji: '🍚', label: 'Gạo nếp', type: 'food' as const },
  { id: 9, emoji: '🍃', label: 'Lá chuối', type: 'tool' as const },
  { id: 10, emoji: '🧂', label: 'Muối', type: 'food' as const },
];

export function Round0ChonDungCu({ onComplete, setBacteriaLevel, bacteriaLevel }: RoundProps) {
  const [toolZone, setToolZone] = useState<number[]>([]);
  const [foodZone, setFoodZone] = useState<number[]>([]);
  const [dragging, setDragging] = useState<number | null>(null);
  const [wrong, setWrong] = useState('');
  const [wrongHint, setWrongHint] = useState('');
  const [phase, setPhase] = useState<'intro' | 'play' | 'done'>('intro');

  const remaining = ALL_ITEMS.filter(i => !toolZone.includes(i.id) && !foodZone.includes(i.id));
  const correctTools = toolZone.filter(id => ALL_ITEMS.find(i => i.id === id)?.type === 'tool').length;
  const wrongInTool = toolZone.filter(id => ALL_ITEMS.find(i => i.id === id)?.type !== 'tool').length;
  const correctFood = foodZone.filter(id => ALL_ITEMS.find(i => i.id === id)?.type === 'food').length;
  const wrongInFood = foodZone.filter(id => ALL_ITEMS.find(i => i.id === id)?.type !== 'food').length;

  const handleDropTool = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragging === null) return;
    const item = ALL_ITEMS.find(i => i.id === dragging);
    if (!item) return;
    if (item.type !== 'tool') {
      setWrong(`💡 Hmm... Vật này có ăn được không? Hãy nghĩ xem nó dùng để ăn hay để nấu nhé!`);
      setWrongHint(`🤔 Gợi ý: Hãy nhìn kỹ lại — đây là thứ em ăn vào hay thứ em dùng khi nấu?`);
      setBacteriaLevel(Math.min(100, bacteriaLevel + 5));
    } else {
      setToolZone(prev => [...prev, dragging]);
      setWrongHint('');
    }
    setDragging(null);
  };

  const handleDropFood = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragging === null) return;
    const item = ALL_ITEMS.find(i => i.id === dragging);
    if (!item) return;
    if (item.type !== 'food') {
      setWrong(`💡 Ủa... Thứ này có ăn được không nhỉ? Hãy suy nghĩ thêm xem nó thuộc nhóm nào!`);
      setWrongHint(`🤔 Gợi ý: Em dùng vật này để làm gì khi nấu ăn — cầm nó hay ăn nó?`);
    } else {
      setFoodZone(prev => [...prev, dragging]);
      setWrongHint('');
    }
    setDragging(null);
  };

  const handleConfirm = () => {
    if (correctTools >= 7 && correctFood >= 3 && wrongInTool === 0 && wrongInFood === 0) {
      setPhase('done');
      setTimeout(() => onComplete(true, 100), 800);
    } else if (remaining.length > 0) {
      setWrong(`Chưa xong! Còn ${remaining.length} đồ vật chưa được phân loại.`);
    } else if (wrongInTool > 0 || wrongInFood > 0) {
      setWrong('Có vật bỏ nhầm ô! Kiểm tra lại nhé!');
    }
  };

  if (phase === 'intro') {
    return (
      <div className="flex flex-col items-center gap-7 p-8 bg-teal-50 rounded-3xl">
        <div className="text-8xl">🍳</div>
        <h3 className="text-3xl font-extrabold text-teal-700 text-center">Phân loại Dụng cụ và Thực phẩm!</h3>
        <p className="text-lg text-teal-600 text-center max-w-lg">Kéo thả từng đồ vật vào đúng ô:&nbsp;<strong>Dụng cụ nấu ăn</strong>&nbsp;hoặc&nbsp;<strong>Thực phẩm nấu ăn</strong></p>
        <div className="grid grid-cols-2 gap-5 text-base w-full max-w-lg">
          <div className="bg-green-100 rounded-2xl p-5">
            <p className="font-extrabold text-green-700 mb-3 text-lg">🔧 Dụng cụ nấu ăn</p>
          </div>
          <div className="bg-orange-100 rounded-2xl p-5">
            <p className="font-extrabold text-orange-700 mb-3 text-lg">🥗 Thực phẩm nấu ăn</p>
          </div>
        </div>
        <button onClick={() => setPhase('play')} className="px-12 py-5 bg-teal-500 text-white font-bold text-xl rounded-2xl cursor-pointer hover:bg-teal-600 whitespace-nowrap">
          Bắt đầu kéo thả! →
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-6 bg-teal-50 rounded-3xl">
      {wrong && <WrongWarning message={wrong} onDismiss={() => setWrong('')} />}
      <h3 className="text-2xl font-extrabold text-teal-700 text-center">🍳 Kéo thả vào đúng ô!</h3>
      <Instruction text="👆 Kéo từng đồ vật vào ô Dụng cụ hoặc Thực phẩm" />
      {wrongHint && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl px-5 py-3 text-base font-semibold text-amber-800 text-center">
          {wrongHint}
        </div>
      )}
      <div className="flex justify-between text-base font-bold px-2">
        <span className="text-green-600">✅ Dụng cụ đúng: {correctTools}/7</span>
        <span className="text-orange-600">✅ Thực phẩm đúng: {correctFood}/3</span>
      </div>
      {/* Items to drag */}
      <div className="grid grid-cols-5 gap-3 min-h-16">
        {remaining.map(item => (
          <div key={item.id} draggable
            onDragStart={() => setDragging(item.id)}
            onDragEnd={() => setDragging(null)}
            className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 cursor-grab active:cursor-grabbing select-none transition-all hover:scale-110 ${
              dragging === item.id ? 'opacity-30 scale-90' : 'border-gray-200 bg-white hover:border-teal-300'
            }`}>
            <span className="text-4xl">{item.emoji}</span>
            <span className="text-sm text-center font-bold text-gray-700 leading-tight">{item.label}</span>
          </div>
        ))}
      </div>
      {/* Drop zones */}
      <div className="grid grid-cols-2 gap-5">
        <div onDragOver={e => e.preventDefault()} onDrop={handleDropTool}
          className="min-h-28 border-2 border-dashed border-green-400 rounded-2xl p-4 bg-green-50 transition-all">
          <p className="text-base font-extrabold text-green-600 mb-3 text-center">🔧 Dụng cụ nấu ăn</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {toolZone.map(id => {
              const item = ALL_ITEMS.find(i => i.id === id);
              return item ? (
                <div key={id} className={`flex items-center gap-1 px-3 py-2 rounded-xl text-base font-bold border ${
                  item.type === 'tool' ? 'bg-green-100 border-green-300 text-green-700' : 'bg-red-100 border-red-300 text-red-700'
                }`}>
                  <span className="text-lg">{item.emoji}</span>
                  <span>{item.label}</span>
                  {item.type !== 'tool' && <span className="text-red-500">⚠️</span>}
                </div>
              ) : null;
            })}
          </div>
        </div>
        <div onDragOver={e => e.preventDefault()} onDrop={handleDropFood}
          className="min-h-28 border-2 border-dashed border-orange-400 rounded-2xl p-4 bg-orange-50 transition-all">
          <p className="text-base font-extrabold text-orange-600 mb-3 text-center">🥗 Thực phẩm nấu ăn</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {foodZone.map(id => {
              const item = ALL_ITEMS.find(i => i.id === id);
              return item ? (
                <div key={id} className={`flex items-center gap-1 px-3 py-2 rounded-xl text-base font-bold border ${
                  item.type === 'food' ? 'bg-orange-100 border-orange-300 text-orange-700' : 'bg-red-100 border-red-300 text-red-700'
                }`}>
                  <span className="text-lg">{item.emoji}</span>
                  <span>{item.label}</span>
                  {item.type !== 'food' && <span className="text-red-500">⚠️</span>}
                </div>
              ) : null;
            })}
          </div>
        </div>
      </div>
      {phase === 'done'
        ? <p className="text-2xl font-extrabold text-green-600 text-center">🎉 Phân loại đúng tất cả rồi!</p>
        : <button onClick={handleConfirm} className="py-5 bg-teal-500 text-white font-bold text-xl rounded-2xl cursor-pointer hover:bg-teal-600 whitespace-nowrap">
            ✅ Kiểm tra kết quả!
          </button>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 3: RỬA TAY (7 bước)
// ══════════════════════════════════════════════════════
const WASH_STEPS = [
  { label: 'Kéo tay vào vòi nước', instruction: '👆 Kéo đôi tay vào vòi nước', sub: 'Kéo thả biểu tượng tay xuống chỗ vòi nước', action: 'drag', icon: '🤲' },
  { label: 'Bấm mở vòi nước', instruction: '👆 Bấm nút mở vòi nước', sub: 'Nhấp vào van vòi để mở nước chảy ra', action: 'click', icon: '💧' },
  { label: 'Kéo xà phòng vào tay', instruction: '👆 Kéo thanh xà phòng vào tay', sub: 'Kéo thả 🧼 vào vùng bàn tay', action: 'drag', icon: '🤲' },
  { label: 'Chà hai lòng bàn tay', instruction: '👆 Giữ chuột và di chuyển qua lại', sub: 'Nhấn giữ chuột rồi kéo ngang để chà tay', action: 'hold-move', icon: '🤲' },
  { label: 'Chà kẽ tay theo mũi tên', instruction: '👆 Kéo chuột theo hướng mũi tên', sub: 'Kéo theo mũi tên ↔ để chà sạch kẽ tay', action: 'arrow', icon: '🤲' },
  { label: 'Kéo tay xuống nước rửa', instruction: '👆 Kéo tay xuống vùng nước để rửa', sub: 'Kéo thả bàn tay vào vùng nước bên dưới', action: 'drag-rinse', icon: '🤲' },
  { label: 'Kéo khăn để lau khô', instruction: '👆 Kéo khăn lau vào tay để lau khô', sub: 'Kéo thả 🧻 vào vùng bàn tay', action: 'drag', icon: '🧻' },
];

export function Round1RuaTay({ onComplete, bacteriaLevel, setBacteriaLevel }: RoundProps) {
  const [step, setStep] = useState(0);
  const [scrubDistance, setScrubDistance] = useState(0);
  const [arrowProgress, setArrowProgress] = useState(0);
  const [hasSoap, setHasSoap] = useState(false);
  const [wrong, setWrong] = useState('');
  const [tapOpen, setTapOpen] = useState(false);
  const [handsAtTap, setHandsAtTap] = useState(false);
  const [draggingHands, setDraggingHands] = useState(false);
  const [draggingSoap, setDraggingSoap] = useState(false);
  const [draggingTowel, setDraggingTowel] = useState(false);
  const [draggingHandsRinse, setDraggingHandsRinse] = useState(false);
  const [done, setDone] = useState(false);
  const [arrowDir, setArrowDir] = useState(1);
  const scrubRef = useRef(false);
  const lastXRef = useRef<number | null>(null);
  const TOTAL_STEPS = WASH_STEPS.length;
  const SCRUB_TARGET = 130;
  const ARROW_TARGET = 6;

  const advance = useCallback(() => {
    const next = step + 1;
    if (next >= TOTAL_STEPS) {
      setDone(true);
      setBacteriaLevel(Math.max(0, bacteriaLevel - 40));
      setTimeout(() => onComplete(true, 100), 1000);
    } else {
      setStep(next);
    }
  }, [step, TOTAL_STEPS, bacteriaLevel, setBacteriaLevel, onComplete]);

  const handleScrubMouseDown = () => { scrubRef.current = true; lastXRef.current = null; };
  const handleScrubMouseMove = useCallback((e: React.MouseEvent) => {
    if (!scrubRef.current || step !== 3) return;
    if (lastXRef.current !== null) {
      const delta = Math.abs(e.clientX - lastXRef.current);
      setScrubDistance(v => {
        const next = Math.min(SCRUB_TARGET, v + delta);
        if (next >= SCRUB_TARGET) {
          scrubRef.current = false;
          setTimeout(advance, 400);
        }
        return next;
      });
    }
    lastXRef.current = e.clientX;
  }, [step, advance]);
  const handleScrubMouseUp = () => { scrubRef.current = false; lastXRef.current = null; };

  const handleArrowMove = useCallback((e: React.MouseEvent) => {
    if (!scrubRef.current || step !== 4) return;
    if (lastXRef.current !== null) {
      const dx = e.clientX - lastXRef.current;
      if (Math.abs(dx) > 8) {
        const expectedDir = arrowDir;
        const actualDir = dx > 0 ? 1 : -1;
        if (actualDir === expectedDir) {
          setArrowProgress(v => {
            const next = v + 1;
            if (next >= ARROW_TARGET) {
              scrubRef.current = false;
              setTimeout(advance, 400);
            }
            if (next % 2 === 0) setArrowDir(d => d * -1);
            return Math.min(ARROW_TARGET, next);
          });
        }
        lastXRef.current = e.clientX;
      }
    } else {
      lastXRef.current = e.clientX;
    }
  }, [step, arrowDir, advance]);

  const handEmoji = done ? '🙌' : hasSoap && step >= 3 ? '🫧' : handsAtTap ? '🤲' : '🖐️';

  return (
    <div className="flex flex-col items-center gap-5 p-6 bg-sky-50 rounded-3xl min-h-96"
      onMouseMove={step === 3 ? handleScrubMouseMove : step === 4 ? handleArrowMove : undefined}
      onMouseUp={[3, 4].includes(step) ? handleScrubMouseUp : undefined}>
      {wrong && <WrongWarning message={wrong} onDismiss={() => setWrong('')} />}
      <h3 className="text-3xl font-extrabold text-sky-700 text-center">🙌 Rửa tay trước khi nấu!</h3>
      <StepDots total={TOTAL_STEPS} current={step} />

      {!done && (
        <div className="bg-sky-100 border-2 border-sky-300 rounded-2xl px-6 py-4 text-center w-full max-w-xl">
          <span className="text-3xl">{WASH_STEPS[step].icon}</span>
          <p className="text-xl font-extrabold text-sky-700 mt-2">Bước {step + 1}: {WASH_STEPS[step].label}</p>
          <p className="text-base text-sky-600 mt-1 font-semibold">{WASH_STEPS[step].instruction}</p>
          <p className="text-base text-sky-500 mt-1 italic">{WASH_STEPS[step].sub}</p>
        </div>
      )}

      {!done && (
        <div className="flex items-center gap-3 text-base">
          <span className="font-bold text-gray-500">🦠 Vi khuẩn:</span>
          <div className="flex gap-1">
            {Array.from({ length: Math.max(0, 5 - step) }).map((_, i) => (
              <span key={i} className="text-xl animate-bounce" style={{ animationDelay: `${i * 0.15}s` }}>🦠</span>
            ))}
            {5 - step <= 0 && <span className="text-green-500 font-extrabold text-base">✅ Không còn vi khuẩn!</span>}
          </div>
        </div>
      )}

      {/* STEP 0: Drag hands to tap */}
      {step === 0 && (
        <div className="flex items-center gap-12 w-full justify-center">
          <div draggable onDragStart={() => setDraggingHands(true)} onDragEnd={() => setDraggingHands(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingHands ? 'opacity-30 scale-90' : 'hover:scale-110 animate-pulse'}`}>
            <span className="text-7xl">🖐️</span>
            <span className="text-base font-extrabold text-sky-600">Kéo tay →</span>
          </div>
          <span className="text-4xl text-sky-300 font-bold">→→</span>
          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setDraggingHands(false); setHandsAtTap(true); setTimeout(advance, 400); }}
            className="flex flex-col items-center gap-2 w-28 h-28 rounded-full border-4 border-dashed border-sky-400 flex items-center justify-center bg-sky-50">
            <span className="text-6xl">🚿</span>
            <span className="text-sm text-sky-500 font-bold">Thả vào đây</span>
          </div>
        </div>
      )}

      {/* STEP 1: Click tap button */}
      {step === 1 && (
        <div className="flex flex-col items-center gap-5">
          <div className="relative">
            <span className="text-8xl">🚿</span>
            <span className="text-6xl ml-2">🤲</span>
          </div>
          <button onClick={() => { setTapOpen(true); setTimeout(advance, 400); }}
            className="px-10 py-5 bg-sky-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-sky-600 whitespace-nowrap animate-pulse">
            🔧 Bấm mở vòi nước!
          </button>
          <p className="text-base text-sky-500 italic">{tapOpen ? 'Vòi đã mở!' : 'Nhấp vào nút để mở van nước chảy ra'}</p>
        </div>
      )}

      {/* STEP 2: Drag soap */}
      {step === 2 && (
        <div className="flex items-center gap-10 w-full justify-center">
          <div draggable onDragStart={() => setDraggingSoap(true)} onDragEnd={() => setDraggingSoap(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingSoap ? 'opacity-30' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-7xl">🧼</span>
            <span className="text-base font-bold text-sky-600">Kéo xà phòng →</span>
          </div>
          <span className="text-4xl text-sky-300 font-bold">→→</span>
          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setDraggingSoap(false); setHasSoap(true); setTimeout(advance, 500); }}
            className="flex flex-col items-center gap-2 w-28 h-28 rounded-full border-4 border-dashed border-yellow-400 items-center justify-center bg-yellow-50">
            <span className="text-5xl">🤲</span>
            <span className="text-sm text-yellow-600 font-bold">Thả vào tay</span>
          </div>
        </div>
      )}

      {/* STEP 3: Hold + scrub move */}
      {step === 3 && (
        <div className="flex flex-col items-center gap-5 w-full max-w-md select-none">
          <div className="bg-white border-2 border-sky-200 rounded-2xl p-4 text-center text-base text-sky-600 font-bold">
            🖱️ Nhấn giữ chuột trái và kéo ngang để chà tay!
          </div>
          <div
            onMouseDown={handleScrubMouseDown}
            className="w-44 h-44 rounded-3xl bg-gradient-to-b from-sky-100 to-sky-200 border-4 border-sky-300 flex items-center justify-center cursor-ew-resize text-7xl select-none hover:border-sky-500 active:bg-sky-300 transition-all"
            style={{ userSelect: 'none' }}>
            🫧
          </div>
          <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sky-400 to-blue-500 rounded-full transition-all"
              style={{ width: `${(scrubDistance / SCRUB_TARGET) * 100}%` }} />
          </div>
          <p className="text-base text-sky-600 font-bold">
            Chà tay: {Math.round((scrubDistance / SCRUB_TARGET) * 100)}% {scrubDistance >= SCRUB_TARGET ? '✅ Đủ rồi!' : '— Tiếp tục chà!'}
          </p>
          <div className="flex items-center gap-6 text-4xl">
            <span className={`transition-all ${scrubRef.current ? 'text-sky-500 scale-125' : 'text-gray-300'}`}>←</span>
            <span className="text-6xl">🤲</span>
            <span className={`transition-all ${scrubRef.current ? 'text-sky-500 scale-125' : 'text-gray-300'}`}>→</span>
          </div>
        </div>
      )}

      {/* STEP 4: Arrow direction drag */}
      {step === 4 && (
        <div className="flex flex-col items-center gap-5 w-full max-w-md select-none">
          <div className="bg-white border-2 border-sky-200 rounded-2xl p-4 text-center text-base text-sky-600 font-bold">
            👆 Nhấn giữ và kéo theo hướng mũi tên để chà kẽ tay!
          </div>
          <div className="flex items-center gap-4">
            <span className={`text-5xl font-extrabold transition-all ${arrowDir > 0 ? 'text-sky-500 scale-150 animate-pulse' : 'text-gray-200'}`}>→</span>
            <div
              onMouseDown={handleScrubMouseDown}
              className="w-40 h-40 rounded-3xl bg-gradient-to-b from-blue-100 to-sky-200 border-4 border-sky-300 flex items-center justify-center text-6xl cursor-ew-resize select-none hover:border-sky-500 active:bg-sky-200 transition-all">
              🤲
            </div>
            <span className={`text-5xl font-extrabold transition-all ${arrowDir < 0 ? 'text-sky-500 scale-150 animate-pulse' : 'text-gray-200'}`}>←</span>
          </div>
          <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-400 to-sky-300 rounded-full transition-all"
              style={{ width: `${(arrowProgress / ARROW_TARGET) * 100}%` }} />
          </div>
          <p className="text-base text-sky-600 font-bold">Kẽ tay: {arrowProgress}/{ARROW_TARGET} lần</p>
        </div>
      )}

      {/* STEP 5: Drag hands to water zone */}
      {step === 5 && (
        <div className="flex flex-col items-center gap-6">
          <div draggable onDragStart={() => setDraggingHandsRinse(true)} onDragEnd={() => setDraggingHandsRinse(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingHandsRinse ? 'opacity-30' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-7xl">🤲</span>
            <span className="text-base font-bold text-sky-600">Kéo tay xuống nước →</span>
          </div>
          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setDraggingHandsRinse(false); setTimeout(advance, 500); }}
            className="w-full max-w-md h-20 rounded-2xl border-4 border-dashed border-blue-400 flex items-center justify-center gap-4 bg-blue-50 text-blue-500 font-bold text-lg py-4">
            <span className="text-4xl">💦</span>
            <span>Thả tay vào đây để rửa</span>
          </div>
        </div>
      )}

      {/* STEP 6: Drag towel */}
      {step === 6 && (
        <div className="flex items-center gap-10 w-full justify-center">
          <div draggable onDragStart={() => setDraggingTowel(true)} onDragEnd={() => setDraggingTowel(false)}
            className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingTowel ? 'opacity-30' : 'hover:scale-110 animate-bounce'}`}>
            <span className="text-7xl">🧻</span>
            <span className="text-base font-bold text-sky-600">Kéo khăn →</span>
          </div>
          <span className="text-4xl text-sky-300 font-bold">→→</span>
          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setDraggingTowel(false); setTimeout(advance, 500); }}
            className="w-28 h-28 rounded-full border-4 border-dashed border-green-400 flex items-center justify-center bg-green-50">
            <span className="text-5xl">{handEmoji}</span>
          </div>
        </div>
      )}

      {done && (
        <div className="flex flex-col items-center gap-4">
          <div className="text-8xl animate-bounce">🙌✨</div>
          <p className="text-2xl font-extrabold text-green-600">Tay sạch rồi!</p>
          <p className="text-lg text-green-500">Vi khuẩn đã bị tiêu diệt hoàn toàn!</p>
        </div>
      )}
    </div>
  );
}
